import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { unifiedRolesCRUDService } from '@/services/unifiedRolesCRUDService';
import api from '@/services/api';
import { toast } from 'react-hot-toast';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline';

interface ModuleWithScreens {
  id: string;
  display_name: string;
  sort_order: number;
  screens: Array<{ id: string; display_name: string; resource_id: string; permission_types: string[] }>;
}

interface RolePermissionsModuleViewProps {
  roleId: string;
}

type PermissionState = 'all' | 'none' | 'partial';

// Visual indicator component for module permission states
const ModulePermissionIndicator: React.FC<{
  state: PermissionState;
  permissionType: string;
  moduleId: string;
  onToggle: (checked: boolean) => void;
}> = ({ state, permissionType, moduleId: _moduleId, onToggle }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Toggle between states: none -> all, partial -> all, all -> none
    const newChecked = state !== 'all';
    onToggle(newChecked);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        relative h-5 w-5 rounded border-2 transition-all duration-200 flex items-center justify-center
        ${state === 'all' 
          ? 'bg-blue-800 border-blue-800' 
          : state === 'partial' 
          ? 'bg-white border-gray-400' 
          : 'bg-white border-gray-300'
        }
        hover:opacity-80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
      `}
      aria-label={`${permissionType}: ${state === 'all' ? 'All screens' : state === 'partial' ? 'Some screens' : 'No screens'}`}
    >
      {state === 'all' && (
        <CheckIcon className="h-3.5 w-3.5 text-white" />
      )}
      {state === 'partial' && (
        <CheckIcon className="h-3.5 w-3.5 text-gray-500" />
      )}
    </button>
  );
};

export const RolePermissionsModuleView: React.FC<RolePermissionsModuleViewProps> = ({ roleId }) => {
  const { t } = useLanguage();
  const [modules, setModules] = useState<ModuleWithScreens[]>([]);
  const [permissionTypes, setPermissionTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);
  const [moduleLevelSelected, setModuleLevelSelected] = useState<Record<string, Set<string>>>({});
  const [screenLevelSelected, setScreenLevelSelected] = useState<Record<string, Record<string, Set<string>>>>({});
  const [_savingModuleId, setSavingModuleId] = useState<string | null>(null);
  const saveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const isInitialLoadRef = useRef(true);
  const modulesRef = useRef<ModuleWithScreens[]>([]);
  const moduleLevelSelectedRef = useRef<Record<string, Set<string>>>({});
  const screenLevelSelectedRef = useRef<Record<string, Record<string, Set<string>>>>({});

  const loadData = async () => {
    if (!roleId) return;
    setLoading(true);
    isInitialLoadRef.current = true; // Set to true before loading
    try {
      const [byModuleRes, availRes] = await Promise.all([
        unifiedRolesCRUDService.getRolePermissionsByModule(roleId),
        api.get('/rbac/available-permissions-for-role-management')
      ]);
      setModules(byModuleRes.modules || []);
      setPermissionTypes(availRes.data?.data?.permission_types || []);
      setModuleLevelSelected({});
      setScreenLevelSelected({});
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
      // Set to false after a short delay to allow state to settle
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    }
  };

  useEffect(() => {
    loadData();
  }, [roleId]);

  // Keep refs in sync with state
  useEffect(() => {
    modulesRef.current = modules;
  }, [modules]);

  useEffect(() => {
    moduleLevelSelectedRef.current = moduleLevelSelected;
  }, [moduleLevelSelected]);

  useEffect(() => {
    screenLevelSelectedRef.current = screenLevelSelected;
  }, [screenLevelSelected]);

  // Auto-save module-level permissions
  const autoSaveModuleLevel = async (moduleId: string) => {
    // Clear existing timeout for this module
    if (saveTimeoutRef.current[moduleId]) {
      clearTimeout(saveTimeoutRef.current[moduleId]);
    }

    // Set new timeout to debounce saves
    saveTimeoutRef.current[moduleId] = setTimeout(async () => {
      if (isInitialLoadRef.current) return;
      
      const selected = moduleLevelSelectedRef.current[moduleId];
      const types = selected ? Array.from(selected) : [];
      
      setSavingModuleId(moduleId);
      try {
        await unifiedRolesCRUDService.saveRolePermissionsByModule(roleId, moduleId, types);
        toast.success(t('roles.permissionsSaved') || 'Permissions saved');
        
        // Update base modules data to reflect the saved state (without full reload)
        setModules(prevModules => {
          return prevModules.map(mod => {
            if (mod.id !== moduleId) return mod;
            return {
              ...mod,
              screens: mod.screens.map(screen => ({
                ...screen,
                permission_types: types
              }))
            };
          });
        });
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Failed to save module permissions');
      } finally {
        setSavingModuleId(null);
        delete saveTimeoutRef.current[moduleId];
      }
    }, 500); // 500ms debounce
  };

  // Auto-save screen-level permissions
  const autoSaveScreenLevel = async (moduleId: string) => {
    // Clear existing timeout for this module
    if (saveTimeoutRef.current[`screen-${moduleId}`]) {
      clearTimeout(saveTimeoutRef.current[`screen-${moduleId}`]);
    }

    // Set new timeout to debounce saves
    saveTimeoutRef.current[`screen-${moduleId}`] = setTimeout(async () => {
      if (isInitialLoadRef.current) return;
      
      // Read from refs which are updated immediately
      const mod = modulesRef.current.find(m => m.id === moduleId);
      if (!mod) return;
      
      const screens = mod.screens.map(s => {
        // Get permissions from refs (most up-to-date)
        const basePermissions = new Set(s.permission_types || []);
        const overrides = screenLevelSelectedRef.current[moduleId]?.[s.resource_id];
        const permissionTypes = overrides !== undefined ? overrides : basePermissions;
        
        return {
          resource_id: s.resource_id,
          permission_types: Array.from(permissionTypes)
        };
      });
      
      setSavingModuleId(moduleId);
      try {
        await unifiedRolesCRUDService.saveRolePermissionsByModuleScreens(roleId, moduleId, screens);
        toast.success(t('roles.permissionsSaved') || 'Permissions saved');
        
        // Update base modules data to reflect the saved state
        setModules(prevModules => {
          return prevModules.map(m => {
            if (m.id !== moduleId) return m;
            return {
              ...m,
              screens: m.screens.map(screen => {
                const overrides = screenLevelSelectedRef.current[moduleId]?.[screen.resource_id];
                const basePerms = new Set(screen.permission_types || []);
                const finalPerms = overrides !== undefined ? overrides : basePerms;
                return {
                  ...screen,
                  permission_types: Array.from(finalPerms)
                };
              })
            };
          });
        });
        
        // Clear overrides for this module since they're now saved in base permissions
        setScreenLevelSelected(prev => {
          const next = { ...prev };
          if (next[moduleId]) {
            const { [moduleId]: removed, ...rest } = next;
            return rest;
          }
          return next;
        });
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Failed to save screen permissions');
      } finally {
        setSavingModuleId(null);
        delete saveTimeoutRef.current[`screen-${moduleId}`];
      }
    }, 500); // 500ms debounce
  };

  const toggleModuleLevel = (moduleId: string, permissionType: string, checked: boolean) => {
    // Update module-level selection
    setModuleLevelSelected(prev => {
      const next = { ...prev };
      if (!next[moduleId]) next[moduleId] = new Set();
      const set = new Set(next[moduleId]);
      if (checked) set.add(permissionType); else set.delete(permissionType);
      next[moduleId] = set;
      moduleLevelSelectedRef.current = next; // Update ref
      return next;
    });

    // Instantly reflect changes at screen level as well
    const mod = modules.find(m => m.id === moduleId);
    if (mod) {
      setScreenLevelSelected(prev => {
        const next = { ...prev };
        if (!next[moduleId]) next[moduleId] = {};
        mod.screens.forEach(screen => {
          // Get current screen permissions (either from overrides or base permissions)
          const existingOverrides = prev[moduleId]?.[screen.resource_id];
          const basePermissions = new Set(screen.permission_types || []);
          const currentTypes = existingOverrides !== undefined 
            ? new Set(existingOverrides) 
            : basePermissions;
          
          // Update the permission type
          if (checked) {
            currentTypes.add(permissionType);
          } else {
            currentTypes.delete(permissionType);
          }
          
          next[moduleId][screen.resource_id] = currentTypes;
        });
        screenLevelSelectedRef.current = next; // Update ref
        return next;
      });
      
      // Auto-save both module and screen level changes
      autoSaveModuleLevel(moduleId);
      autoSaveScreenLevel(moduleId);
    } else {
      autoSaveModuleLevel(moduleId);
    }
  };

  const toggleScreenLevel = (moduleId: string, resourceId: string, permissionType: string, checked: boolean) => {
    // Get current permissions for this screen
    const currentPermissions = getScreenPermissionTypes(moduleId, resourceId);
    
    // Create new Set with the toggled permission
    const newPermissions = new Set(currentPermissions);
    if (checked) {
      newPermissions.add(permissionType);
    } else {
      newPermissions.delete(permissionType);
    }
    
    // Update state - this will trigger UI update
    setScreenLevelSelected(prev => {
      const next = { ...prev };
      if (!next[moduleId]) next[moduleId] = {};
      next[moduleId] = { ...next[moduleId] };
      next[moduleId][resourceId] = newPermissions;
      
      // Update ref immediately for saving
      screenLevelSelectedRef.current = next;
      
      return next;
    });
    
    // Auto-save screen-level changes
    autoSaveScreenLevel(moduleId);
  };

  const setModuleLevelAll = (moduleId: string, checked: boolean) => {
    // Update module-level selection
    setModuleLevelSelected(prev => {
      const next = {
        ...prev,
        [moduleId]: checked ? new Set(permissionTypes) : new Set<string>()
      };
      moduleLevelSelectedRef.current = next; // Update ref
      return next;
    });

    // Instantly reflect changes at screen level as well
    const mod = modules.find(m => m.id === moduleId);
    if (mod) {
      setScreenLevelSelected(prev => {
        const next: Record<string, Record<string, Set<string>>> = { ...prev };
        if (!next[moduleId]) next[moduleId] = {};
        mod.screens.forEach(screen => {
          // Set all permission types for each screen
          next[moduleId][screen.resource_id] = checked ? new Set(permissionTypes) : new Set<string>();
        });
        screenLevelSelectedRef.current = next; // Update ref
        return next;
      });
      
      // Auto-save both module and screen level changes
      autoSaveModuleLevel(moduleId);
      autoSaveScreenLevel(moduleId);
    } else {
      autoSaveModuleLevel(moduleId);
    }
  };

  const setScreenLevelAll = (moduleId: string, resourceId: string, checked: boolean) => {
    const newPermissions = checked ? new Set(permissionTypes) : new Set<string>();
    
    setScreenLevelSelected(prev => {
      // Create completely new object structure to ensure React detects change
      const next: Record<string, Record<string, Set<string>>> = { ...prev };
      const newModuleLevel: Record<string, Set<string>> = { ...(next[moduleId] || {}) };
      newModuleLevel[resourceId] = newPermissions;
      next[moduleId] = newModuleLevel;

      // Update ref immediately for immediate UI updates
      screenLevelSelectedRef.current = next;

      return next;
    });
    
    // Auto-save screen-level changes
    autoSaveScreenLevel(moduleId);
  };


  const getScreenPermissionTypes = (moduleId: string, resourceId: string) => {
    // Always read from refs first (most up-to-date), then fallback to state
    const mod = modulesRef.current.find(m => m.id === moduleId);
    if (!mod) {
      // Fallback to state if ref not available
      const modFromState = modules.find(m => m.id === moduleId);
      const screen = modFromState?.screens.find(s => s.resource_id === resourceId);
      const basePermissions = new Set(screen?.permission_types || []);
      const overrides = screenLevelSelected[moduleId]?.[resourceId];
      if (overrides !== undefined) return overrides;
      return basePermissions;
    }
    
    const screen = mod.screens.find(s => s.resource_id === resourceId);
    const basePermissions = new Set(screen?.permission_types || []);
    const overrides = screenLevelSelectedRef.current[moduleId]?.[resourceId];
    if (overrides !== undefined) return overrides;
    return basePermissions;
  };

  // Calculate permission state for a module/permission type combination
  const getModulePermissionState = (moduleId: string, permissionType: string): PermissionState => {
    const mod = modules.find(m => m.id === moduleId);
    if (!mod || mod.screens.length === 0) return 'none';

    let hasPermissionCount = 0;
    
    mod.screens.forEach(screen => {
      const screenTypes = getScreenPermissionTypes(moduleId, screen.resource_id);
      if (screenTypes.has(permissionType)) {
        hasPermissionCount++;
      }
    });

    if (hasPermissionCount === 0) return 'none';
    if (hasPermissionCount === mod.screens.length) return 'all';
    return 'partial';
  };

  // Calculate "All" permission state for a module (all permission types for all screens)
  const getModuleAllPermissionState = (moduleId: string): PermissionState => {
    const mod = modules.find(m => m.id === moduleId);
    if (!mod || mod.screens.length === 0 || permissionTypes.length === 0) return 'none';

    let allScreensHaveAllTypes = 0;
    let someScreensHaveSomeTypes = 0;

    mod.screens.forEach(screen => {
      const screenTypes = getScreenPermissionTypes(moduleId, screen.resource_id);
      const hasAllTypes = permissionTypes.every(pt => screenTypes.has(pt));
      const hasSomeTypes = permissionTypes.some(pt => screenTypes.has(pt));
      
      if (hasAllTypes) {
        allScreensHaveAllTypes++;
      }
      if (hasSomeTypes) {
        someScreensHaveSomeTypes++;
      }
    });

    if (allScreensHaveAllTypes === mod.screens.length) return 'all';
    if (someScreensHaveSomeTypes === 0) return 'none';
    return 'partial';
  };

  // Calculate "All" permission state for a specific screen
  const getScreenAllPermissionState = (moduleId: string, resourceId: string): PermissionState => {
    if (permissionTypes.length === 0) return 'none';
    const screenTypes = getScreenPermissionTypes(moduleId, resourceId);
    const hasAll = permissionTypes.every(pt => screenTypes.has(pt));
    const hasSome = permissionTypes.some(pt => screenTypes.has(pt));
    
    if (hasAll) return 'all';
    if (hasSome) return 'partial';
    return 'none';
  };

  if (loading) {
    return (
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">{t('roles.permissions')}</h3>
        <p className="text-sm text-gray-500">Loading permissions…</p>
      </div>
    );
  }

  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-semibold mb-2">{t('roles.permissions')}</h3>
      <p className="text-sm text-gray-500 mb-4">
        {t('roles.permissionsModuleHint') || 'Assign permissions at module level (applies to all screens in the module), or expand a module to set permissions per screen.'}
      </p>
      <div className="space-y-2">
        {modules.map(module => {
          const expanded = expandedModuleId === module.id;
          return (
            <div key={module.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between gap-4 p-3 bg-gray-50 flex-wrap">
                <button
                  type="button"
                  onClick={() => setExpandedModuleId(expanded ? null : module.id)}
                  className="flex items-center gap-2 text-left font-medium text-gray-900"
                >
                  {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                  {module.display_name}
                  <span className="text-xs font-normal text-gray-500">({module.screens.length} screens)</span>
                </button>
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-1.5 text-sm font-medium cursor-pointer">
                    <ModulePermissionIndicator
                      state={getModuleAllPermissionState(module.id)}
                      permissionType="All"
                      moduleId={module.id}
                      onToggle={(checked: boolean) => setModuleLevelAll(module.id, checked)}
                    />
                    <span>{t('roles.allPermissionTypes') || 'All'}</span>
                  </label>
                  {permissionTypes.map(pt => {
                    const permissionState = getModulePermissionState(module.id, pt);
                    return (
                      <label key={pt} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <ModulePermissionIndicator
                          state={permissionState}
                          permissionType={pt}
                          moduleId={module.id}
                          onToggle={(checked: boolean) => toggleModuleLevel(module.id, pt, checked)}
                        />
                        <span>{pt}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {expanded && (
                <div className="p-3 border-t bg-white">
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {module.screens.map(screen => {
                      const screenTypes = getScreenPermissionTypes(module.id, screen.resource_id);
                      const screenAllState = getScreenAllPermissionState(module.id, screen.resource_id);
                      const screenPermissionState = (pt: string) => {
                        if (screenTypes.has(pt)) return 'all';
                        return 'none';
                      };
                      return (
                        <div key={screen.resource_id} className="flex items-center justify-between gap-4 p-3 bg-gray-50 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">{screen.display_name}</span>
                          <div className="flex items-center gap-4 flex-wrap">
                            <label className="flex items-center gap-1.5 text-sm font-medium cursor-pointer">
                              <ModulePermissionIndicator
                                state={screenAllState}
                                permissionType="All"
                                moduleId={module.id}
                                onToggle={(checked: boolean) => setScreenLevelAll(module.id, screen.resource_id, checked)}
                              />
                              <span>{t('roles.allPermissionTypes') || 'All'}</span>
                            </label>
                            {permissionTypes.map(pt => {
                              const permissionState = screenPermissionState(pt);
                              return (
                                <label key={pt} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                  <ModulePermissionIndicator
                                    state={permissionState}
                                    permissionType={pt}
                                    moduleId={module.id}
                                    onToggle={(checked: boolean) => toggleScreenLevel(module.id, screen.resource_id, pt, checked)}
                                  />
                                  <span>{pt}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {modules.length === 0 && (
        <p className="text-sm text-gray-500">{t('roles.noModules') || 'No modules with screens found.'}</p>
      )}
    </div>
  );
};
